import React from "react";
import { useLocation } from "react-router-dom";
import {
    SpsTextInput,
    SpsLabel,
    useSpsForm,
    SpsButton,
    usePatchReducer,
    SpsSpinner,
} from "@spscommerce/ds-react";
import { ButtonKind, ButtonType } from "@spscommerce/ds-shared";
import { AppRouteProps } from "../../App";
import StatusState from "../../components/StatusState";
import { PasswordPolicyInterface } from "../../interfaces/PasswordPolicy_interface";
import { ValidationCheckInterface } from "../../interfaces/ValidationCheck_interface";
import * as v from "../../utils/validation";
import PasswordPolicy from "../../components/PasswordPolicy";
import { SpsPasswordInput } from "../../components/SpsPasswordInput";
import { useSpsLanguage } from "@spscommerce/i18n";

export type CreateProps = Partial<AppRouteProps> &
    Pick<AppRouteProps, "inviteService" | "redirectService" | "authService" | "env">;

export type InviteDetails = {
    first_name: string;
    last_name: string;
    password: string;
    signed_invite: string;
};

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export function Create({ inviteService, redirectService, authService, env }: CreateProps) {
    const { t } = useSpsLanguage();
    const [state, patchState] = usePatchReducer<{
        internalUserError: boolean;
        reset: boolean;
        resetError: boolean;
        spinning: boolean;
        submitInviteError: boolean;
        submit: boolean;
        successful: boolean;
        prefetchError: boolean;
        prefetchErrorHeader: string;
        prefetchErrorMsg: string;
        passwordPolicy: PasswordPolicyInterface;
        validationCheck: ValidationCheckInterface;
        confirmCreateAccount: boolean;
        showProceedToLoginBtn: boolean;
        showSupportInfo: boolean;
    }>({
        internalUserError: false,
        reset: false,
        resetError: false,
        spinning: true,
        submitInviteError: false,
        submit: false,
        successful: false,
        prefetchError: false,
        prefetchErrorHeader: t("cpauth.oops", {
            defaultValue: "Oops! We're Having a Problem on Our End",
        }),
        prefetchErrorMsg: "",
        passwordPolicy: {
            data: {
                data: {
                    info_message: "",
                    regex_policy: {
                        lower: {
                            error: "",
                            pattern: "",
                        },
                        minlength: {
                            error: "",
                            pattern: "",
                        },
                        number: {
                            error: "",
                            pattern: "",
                        },
                        special: {
                            error: "",
                            pattern: "",
                        },
                        upper: {
                            error: "",
                            pattern: "",
                        },
                    },
                },
            },
        },
        validationCheck: {
            lower: false,
            minlength: false,
            number: false,
            special: false,
            upper: false,
        },
        confirmCreateAccount: false,
        showProceedToLoginBtn: true,
        showSupportInfo: false,
    });
    const queryParams = useQuery();
    // get returns null if prop is undefined, to avoid TS errors
    // we need to set up empty string if null
    const signed_invite_id = queryParams.get("signed_invite") || "";
    const invite_id = queryParams.get("invite") || "";
    const inviteId = signed_invite_id ? signed_invite_id : invite_id;
    const inviteType = signed_invite_id ? "signed" : "unsigned";
    const analyticsUser = queryParams.get("analytics_user") || false;

    const { formValue, formMeta, updateForm } = useSpsForm({
        firstName: "",
        lastName: "",
        password: "",
        email: "",
    });

    React.useEffect(() => {
        getData();
        // eslint-disable-next-line
    }, []);

    async function getData() {
        await getInviteData();
        await getPasswordPolicy();
        patchState({ spinning: false });
    }

    async function getInviteData() {
        try {
            const invite: any = await inviteService.getInvite(inviteId);
            updateForm({
                ...formValue,
                firstName: invite?.firstName,
                lastName: invite?.lastName,
                email: invite?.email,
            });
        } catch (e) {
            console.error("Could not fetch invite data.", e);
            const err = e as any;
            const errorData = err?.response?.data;
            const isCompletedOrExpired =
                errorData?.error_description?.[0] ===
                    "This invitation has already been completed" ||
                errorData?.error_description?.[0] === "This invitation is expired";

            patchState({
                spinning: false,
                prefetchError: true,
                prefetchErrorMsg: isCompletedOrExpired
                    ? ""
                    : t("cpauth.createaccount.fetchInviteError", {
                          defaultValue: "Invite data could not be retrieved",
                      }) +
                      ". " +
                      t("cpauth.createaccount.fetchInviteError.body", {
                          defaultValue:
                              "Already signed up? Have you tried logging in or resetting your password?",
                      }),
                showProceedToLoginBtn: true,
                showSupportInfo: isCompletedOrExpired,
                prefetchErrorHeader:
                    errorData?.error_description?.[0] ===
                    "This invitation has already been completed"
                        ? t("cpauth.createaccount.fetchInviteError.completed", {
                              defaultValue: "This invitation has already been completed",
                          })
                        : errorData?.error_description?.[0] === "This invitation is expired"
                        ? t("cpauth.createaccount.fetchInviteError.expired", {
                              defaultValue: "This invitation is expired",
                          })
                        : state.prefetchErrorHeader,
            });
        }
    }

    async function getPasswordPolicy() {
        try {
            const policy: any = await authService.getPasswordPolicy();
            let policyData: PasswordPolicyInterface = policy.data;
            patchState({ passwordPolicy: policyData });
        } catch (e) {
            console.error("Could not retrieve password policy.", e);
            patchState({
                prefetchError: true,
                prefetchErrorMsg: t("cpauth.createaccount.prefetchPolicyError", {
                    defaultValue:
                        "Cannot retrieve the password policy. Please contact customer support.",
                }),
            });
        }
    }

    async function createUser(e: React.FormEvent) {
        e.preventDefault();
        patchState({ submit: true });
        let details: InviteDetails = {
            first_name: formValue.firstName,
            last_name: formValue.lastName,
            password: formValue.password,
            signed_invite: signed_invite_id,
        };
        try {
            await inviteService.finalizeInvite(details);
            patchState({ successful: true });
        } catch (e) {
            console.error("user creation error", e);
            patchState({ submitInviteError: true });
        } finally {
            patchState({ submit: false });
        }
    }

    function validation(validator: string, currentValue: string): boolean {
        return v.validatePassword(state.passwordPolicy, validator, currentValue);
    }

    function validateAll(currentValue: string) {
        return v.validateAll(state.passwordPolicy, currentValue);
    }

    function runValidationCheck(value: string) {
        let newStateObj: any = {};
        for (const key in state.validationCheck) {
            newStateObj[key] = validation(key, value);
        }
        patchState({ validationCheck: newStateObj });
    }

    function goToTrainingCenter() {
        redirectService.goToTrainingCenter(env);
    }

    function backToLogin() {
        redirectService.backToLogin();
    }

    return (
        <>
            <div className="auth-box">
                <div className="auth-logo">
                    <div className="logo"></div>
                </div>
                <div className="auth-content">
                    {state.spinning && <SpsSpinner />}
                    {state.prefetchError && (
                        <StatusState
                            header={state.prefetchErrorHeader}
                            message={state.prefetchErrorMsg}
                            backToLogin={state.showProceedToLoginBtn ? backToLogin : undefined}
                            supportInfo={state.showSupportInfo}
                            imgSrc="/assets/image-error-6.svg"
                            alt="Error Status Icon"
                        />
                    )}
                    {state.submitInviteError && (
                        <StatusState
                            header={t("cpauth.oops", {
                                defaultValue: "Oops! We're Having a Problem on Our End",
                            })}
                            message={
                                t("cpauth.invalidLink.body", {
                                    defaultValue: "This invitation link appears to be invalid.",
                                }) +
                                " " +
                                t("cpauth.invalidLink.troubleSigningIn", {
                                    defaultValue:
                                        "Trouble signing in? Have you tried resetting your password?",
                                })
                            }
                            backToLogin={backToLogin}
                            imgSrc="/assets/image-error-6.svg"
                            alt="Error Status Icon"
                        />
                    )}
                    {state.successful && (
                        <StatusState
                            header={t("cpauth.createaccount.successful.heading", {
                                defaultValue: "Registration Successfully Completed",
                            })}
                            message={
                                analyticsUser
                                    ? t("cpauth.createaccount.successful.bodyTrainingCenter", {
                                          defaultValue:
                                              "You're ready to go! Click the button below to log in to Training Center.",
                                      })
                                    : t("cpauth.createaccount.successful.body", {
                                          defaultValue:
                                              "You're ready to go! Click the button below to log in to Commerce Platform.",
                                      })
                            }
                            backToLogin={analyticsUser ? goToTrainingCenter : backToLogin}
                            imgSrc="/assets/image-success.svg"
                            alt="Success Status Icon"
                        />
                    )}
                    {!state.prefetchError &&
                        !state.submitInviteError &&
                        !state.successful &&
                        !state.spinning && (
                            <>
                                <h2 className="auth-content-header">
                                    {t("cpauth.createaccount.createan", {
                                        defaultValue: "Create an account",
                                    })}
                                </h2>
                                <SpsLabel for={formMeta.fields.email}>
                                    {t("cpauth.email2", {
                                        defaultValue: "Email",
                                    })}
                                </SpsLabel>
                                <SpsTextInput
                                    className="mb-2"
                                    value={formValue.email}
                                    formMeta={formMeta.fields.email}
                                    data-testid="email-input"
                                    disabled
                                    title={formValue.email}
                                />
                                {/* <p className="auth-content-info" data-testid="create-account-info">
                                    {t("cpauth.createaccount.infoPt1", {
                                        defaultValue:
                                            "If this is not your email address, stop this process and",
                                    })}{" "}
                                    <Link
                                        to={`/request-invitation/?${queryParams}`}
                                        className="auth-underlined-link"
                                    >
                                        {t("cpauth.createaccount.requestInvitation", {
                                            defaultValue: "Request an Invitation",
                                        })}
                                    </Link>{" "}
                                    {t("cpauth.createaccount.infoPt2", {
                                        defaultValue: "for your own account",
                                    })}
                                    .
                                </p> */}
                                {/* {!state.confirmCreateAccount ? (
                                    <SpsButton
                                        data-testid="confirm-create-account-button"
                                        className="mr-1 mb-1 mt-1 auth-button"
                                        kind={ButtonKind.CONFIRM}
                                        onClick={() => patchState({ confirmCreateAccount: true })}
                                    >
                                        Next
                                    </SpsButton>
                                ) : ( */}
                                <form onSubmit={createUser} data-testid="create-account-form">
                                    <div className="auth-form-group">
                                        <SpsLabel for={formMeta.fields.firstName}>
                                            {t("cpauth.firstname", {
                                                defaultValue: "First Name",
                                            })}
                                        </SpsLabel>
                                        <SpsTextInput
                                            className="mb-2"
                                            value={formValue.firstName}
                                            formMeta={formMeta.fields.firstName}
                                            data-testid="firstName-input"
                                        />
                                        <SpsLabel for={formMeta.fields.lastName}>
                                            {t("cpauth.lastname", {
                                                defaultValue: "Last Name",
                                            })}
                                        </SpsLabel>
                                        <SpsTextInput
                                            className="mb-2"
                                            value={formValue.lastName}
                                            formMeta={formMeta.fields.lastName}
                                            data-testid="lastName-input"
                                        />
                                        <SpsLabel for={formMeta.fields.password}>
                                            {t("cpauth.password", { defaultValue: "Password" })}
                                        </SpsLabel>
                                        <SpsPasswordInput
                                            value={formValue.password}
                                            formMeta={formMeta.fields.password}
                                            className="mb-2"
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                runValidationCheck(event.target.value);
                                            }}
                                        />
                                        <PasswordPolicy validationCheck={state.validationCheck} />
                                    </div>
                                    <br />
                                    <div>
                                        <SpsButton
                                            data-testid="create-submit-button"
                                            className="mr-1 mb-1 mt-1 auth-button"
                                            disabled={!validateAll(formValue.password)}
                                            spinning={state.submit}
                                            kind={ButtonKind.CONFIRM}
                                            type={ButtonType.SUBMIT}
                                        >
                                            {t("cpauth.createaccount.createmy", {
                                                defaultValue: "Create My Account",
                                            })}
                                        </SpsButton>
                                    </div>
                                </form>
                                {/* )} */}
                            </>
                        )}
                </div>
            </div>
        </>
    );
}
